import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SidebarStore {
  // Expansion state for projects and folders
  expandedProjects: Set<string>;
  expandedFolders: Set<string>;

  // Actions
  toggleProject: (projectId: string) => void;
  toggleFolder: (folderKey: string) => void;
  expandProject: (projectId: string) => void;
  expandFolder: (folderKey: string) => void;
  collapseProject: (projectId: string) => void;
  collapseFolder: (folderKey: string) => void;
  expandProjectAndParents: (projectId: string, folderIds?: string[]) => void;
  clearExpanded: () => void;
}

export const useSidebarStore = create<SidebarStore>()(
  persist(
  (set) => ({
      // Initial state
      expandedProjects: new Set<string>(),
      expandedFolders: new Set<string>(),

      // Toggle actions
      toggleProject: (projectId: string) => {
    set((state) => {
          const next = new Set(state.expandedProjects);
          if (next.has(projectId)) {
      next.delete(projectId);
          } else {
      next.add(projectId);
          }
          return { expandedProjects: next };
    });
      },

      toggleFolder: (folderKey: string) => {
    set((state) => {
          const next = new Set(state.expandedFolders);
          if (next.has(folderKey)) {
      next.delete(folderKey);
          } else {
      next.add(folderKey);
          }
          return { expandedFolders: next };
    });
      },

      // Expand actions
      expandProject: (projectId: string) => {
    set((state) => {
          const next = new Set(state.expandedProjects);
          next.add(projectId);
          return { expandedProjects: next };
    });
      },

      expandFolder: (folderKey: string) => {
    set((state) => {
          const next = new Set(state.expandedFolders);
          next.add(folderKey);
          return { expandedFolders: next };
    });
      },

      // Collapse actions
      collapseProject: (projectId: string) => {
    set((state) => {
          const next = new Set(state.expandedProjects);
          next.delete(projectId);
          return { expandedProjects: next };
    });
      },

      collapseFolder: (folderKey: string) => {
    set((state) => {
          const next = new Set(state.expandedFolders);
          next.delete(folderKey);
          return { expandedFolders: next };
    });
      },

      // Expand project and all parent folders
      expandProjectAndParents: (projectId: string, folderIds?: string[]) => {
    set((state) => {
          const nextProjects = new Set(state.expandedProjects);
          const nextFolders = new Set(state.expandedFolders);

          nextProjects.add(projectId);

          if (folderIds) {
      folderIds.forEach(folderId => {
              nextFolders.add(folderId);
      });
          }

          return {
      expandedProjects: nextProjects,
      expandedFolders: nextFolders,
          };
    });
      },

      // Clear all expanded state
      clearExpanded: () => {
    set({
          expandedProjects: new Set(),
          expandedFolders: new Set(),
    });
      },
  }),
  {
      name: 'zenda-sidebar-store',
      partialize: (state) => ({
    // Convert Sets to Arrays for serialization
    expandedProjects: Array.from(state.expandedProjects),
    expandedFolders: Array.from(state.expandedFolders),
      }),
      onRehydrateStorage: () => (state) => {
    // Convert Arrays back to Sets after rehydration
    if (state?.expandedProjects && !(state.expandedProjects instanceof Set)) {
          state.expandedProjects = new Set(state.expandedProjects as string[]);
    }
    if (state?.expandedFolders && !(state.expandedFolders instanceof Set)) {
          state.expandedFolders = new Set(state.expandedFolders as string[]);
    }
      },
  }
  )
);


